import React, { useState, useMemo, useCallback } from 'react';
import { computed, observable, action, values, entries, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';

import SearchableComboBox from 'src/components/base/SearchableComboBox';

import PushFlowstep from 'src/components/Flowsteps/PushData/PushFlowstep';
import Step from 'src/components/Flowsteps/base/Step';

import * as Mapping from 'src/components/Flowsteps/PushData/Mapping';
import { CheckboxSetting } from 'src/components/Flowsteps/PushData/Mapping/common';

import { Type, generateFlowstepName } from 'shared/utils/flowstep';
import { KnownObjects } from 'shared/utils/queryAnalysis';


@inject('salesforceStore', 'dataStore', 'officeStore', 'userStore')
export class ConvertLeads extends PushFlowstep {
  @observable mappings = {
    contact: { objectName: KnownObjects.Contact },
    opportunity: { objectName: KnownObjects.Opportunity },
    account: { objectName: KnownObjects.Account },
    relatedPersonAccount: { objectName: KnownObjects.Account },
  };

  @observable conversion = {
    columnIndexes: {
      leadId: null,
      convertedStatus: null,
      ownerId: null,
    },
    options: {
      bypassAccountDedupeCheck: false,
      bypassContactDedupeCheck: false,
      overwriteLeadSource: false,
      sendNotificationEmail: false,
      doNotCreateOpportunity: false,
    },
  };

  constructor(props) {
    super(props, { type: Type.ConvertLeads, types: [Type.ConvertLeads] });
    makeObservable(this);
    
    this.mappings = entries(this.mappings).reduce((out, [key, value]) => {
      const { objectName } = value;

      const mgr = new Mapping.Manager({ salesforceStore: this.props.salesforceStore }, { onSetDirty: this.setDirty });
      mgr.setType(this.type);
      mgr.setObjectName(objectName);

      return Object.assign(out, {
        [key]: {
          manager: mgr,
          objectName,
          error: '',
        }
      });

    }, {});

    const allReactions = [...super.setupBaseReactions()];
    this.cleanReactions = () => allReactions.forEach(dispose => dispose());
  }

  @computed get _generatedName_() {
    const { worksheetName, targetCell: target } = this.selectionTarget || {};
    return generateFlowstepName({ type: this.type, worksheetName, target });
  }

  @computed get columnErrors() {
    return this.comboboxes.reduce((out, x) => {
      const { mandatory, id } = x;
      const index = this.conversion.columnIndexes[id];
      out[id] = (mandatory && index === null) || (index !== null && index > this.excelHeaders.length - 1)
        ? 'Must be mapped to some column'
        : '';
      return out;
    }, {});
  }

  @computed get areIndexesValid() {
    return Object.values(this.columnErrors).every(x => !x);
  }

  @computed get areMappingsValid() {
    return entries(this.mappings).every(([key, value]) => {
      return (value.objectName === KnownObjects.Opportunity && this.conversion.options.doNotCreateOpportunity)
        || !value.manager.status.hasAnyError;
    });
  }

  @computed get _isValid_() {
    return this.excelHeaders.length && this.areIndexesValid && this.areMappingsValid && super.areAllOptionsValid;
  }

  @computed get excelHeaderOptionsStrict() {
    return this.excelHeaders
      .map((x, index) => ({ key: index, text: x, index }));
  }

  @computed get canOpenMappingPage() {
    return this.excelHeaders.length;
  }

  @computed get excelHeaderOptionsUnstrict() {
    return [
      { key: 'not-selected', text: 'Not Selected', index: null },
      ...this.excelHeaderOptionsStrict,
    ];
  }

  @computed get comboboxes() {
    return [
      { label: 'Lead ID column', id: 'leadId', mandatory: true },
      { label: 'Convert Statuses column', id: 'convertedStatus', mandatory: true },
      { label: 'Owner ID column (optional)', id: 'ownerId', mandatory: false },
    ];
  }

  @computed get excelHeadersToIgnore() {
    return entries(this.conversion.columnIndexes).reduce((out, [columnName, index]) => {
      index !== null && out.push(index);
      return out;
    }, []);
  }

  @computed get checkboxes() {
    return [
      { id: 'bypassAccountDedupeCheck', label: 'Bypass account dedupe check', description: `` },
      { id: 'bypassContactDedupeCheck', label: 'Bypass contact dedupe check', description: `` },
      { id: 'overwriteLeadSource', label: 'Overwrite lead source', description: `Specifies whether to overwrite the LeadSource field on the target Contact object with the contents of the LeadSource field in the source Lead object (true), or not (false, the default). To set this field to true, the client application must specify a contactId for the target contact.`},
      { id: 'sendNotificationEmail', label: 'Send notification email', description: `Specifies whether to send a notification email to the owner specified in the ownerId.` },
      { id: 'doNotCreateOpportunity', label: 'Do not create opportunity', description: `Specifies whether to create an Opportunity during lead conversion (false, the default) or not (true). Set this flag to true only if you don't want to create an opportunity from the lead. An opportunity is created by default.`},
    ];
  }

  @computed get mappingButtons() {
    return [
      { id: 'contact', isDisabled: false, buttonLabel: 'Contact', },
      { id: 'opportunity', isDisabled: this.conversion.options.doNotCreateOpportunity, buttonLabel: 'Opportunity', },
      { id: 'account', isDisabled: false, buttonLabel: 'Account', },
      { id: 'relatedPersonAccount', isDisabled: false, buttonLabel: 'Related Person Account' },
    ];
  }

  _renderCustomSteps_ = ({ getStepTitle }) => {
    return (
      <>
        <Step title={getStepTitle('Select column with IDs')}>
          { this.comboboxes.map(({ label, id, mandatory }) => {
            const key = this.conversion.columnIndexes[id];
            const message = this.columnErrors[id];
            return (
              <SearchableComboBox
                key={id}
                label={label}
                placeholder="Select column"
                selectedKey={key}
                onChange={(event, item) => this.handleColumnIndexOptionChange(id, item.index)}
                options={mandatory ? this.excelHeaderOptionsStrict : this.excelHeaderOptionsUnstrict}
                disabled={!this.excelHeaderOptionsStrict.length || this._readOnly_}
                errorMessage={message}
                />
              );
            }
            )}
        </Step>

        <Step title={getStepTitle('Set up mappings (optional)')}>
          { this.mappingButtons.map(({ id, isDisabled, buttonLabel }) => {
            const { manager } = this.mappings[id];
            return <Mapping.Button
              id={id}
              mappingManager={manager}
              label={buttonLabel}
              isDisabled={!this.canOpenMappingPage || isDisabled}
              isEditingDisabled={this._readOnly_}
              showError={!isDisabled}
            />
          })
          }
        </Step>

        <Step title={getStepTitle('Set up parameters')}>
          { this.checkboxes.map(({ label, id, description }) => {
            return (
              <CheckboxSetting
                key={id}
                title={label}
                text={description}
                selected={this.conversion.options[id]}
                disabled={this._readOnly_}
                onChange={(event, selected) => this.handleConversionOptionChange(id, selected)}
              />
            );
          }
          )}
        </Step>
      </>
    );
  }

  @action _setSelectionTarget_(selectionTarget) {
    super._setSelectionTarget_(selectionTarget);
    values(this.mappings).forEach(x => x.manager.setExcelHeaders(this.excelHeaders));
  }

  async _loadFlowstep_(flowstep, isExact = true) {
    const { conversion } = flowstep.options;
    const { mappings = {}, ...restConversion } = conversion;
    this.conversion = { ...this.conversion, ...restConversion };
    
    this.updateHeadersToIgnore();
    Object.entries(mappings).forEach(([key, value]) => {
      this.mappings[key].manager.setMapping(value, { isExact });
    });

    isExact && super.loadFlowstep(flowstep, isExact);
  }

  async _makeFlowstep_(makeCopy) {
    const flowstep = super.makeFlowstep(makeCopy);
    const mappings = entries(this.mappings).reduce((out, [key, value]) => {
      return Object.assign(out, { [key]: value.manager.makeMapping() });
    }, {});

    Object.assign(flowstep.options, {
      conversion: { ...this.conversion, mappings }
    });

    return flowstep;
  }

  @action handleColumnIndexOptionChange(id, value) {
    this.conversion.columnIndexes[id] = value;
    this.updateHeadersToIgnore();
    this.setDirty();
  }

  @action handleConversionOptionChange(id, value) {
    this.conversion.options[id] = value;
    this.setDirty();
  }

  updateHeadersToIgnore() {
    values(this.mappings).forEach(mapping => {
      mapping.manager.setExcelHeadersToIgnore(this.excelHeadersToIgnore)
    });
  }
}